import { useMemo } from 'react';
import { TaxiTable } from '@/components/TaxiTable';
import GroupsIcon from '@mui/icons-material/Groups';
import WalletIcon from '@mui/icons-material/Wallet';
import CastIcon from '@mui/icons-material/Cast';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import { Trans, useTranslation } from 'react-i18next';
// import { TotalPointsChart } from '@/features/taxi-stats';
import {
  useGetBudgetQuery,
  useGetScreensSummaryQuery,
} from '@/models/company-screens';
import { CardStats } from '@/features/card-stats';
import { useProfileQuery } from '@/models/user';
import { Typography } from '@mui/material';
import { useGetPaymentsPartnerTotalSumQuery } from '@/models/payments';
import { Card } from '@/features/card';

export const PartnerHome = () => {
  const { data } = useProfileQuery();
  const companyName = data?.companyName;
  const { data: summaryData } = useGetScreensSummaryQuery();
  const { data: totalSumData } = useGetPaymentsPartnerTotalSumQuery();
  const { data: budgetData } = useGetBudgetQuery();
  const { t } = useTranslation();

  const totalMtdPoints = summaryData?.total_mtd_points || 0;
  const totalMtdPointsAll = summaryData?.total_mtd_points_all || 0;
  const totalScreensOnline = summaryData?.total_screens_online || 0;
  const totalMtdPassengers = summaryData?.total_mtd_passengers || 0;
  const currentMonthBudget = budgetData?.[0]?.budget || '0';
  const previousMonthBudget = budgetData?.[1]?.budget || '0';
  const totalEarnedWithUs = totalSumData?.amount || 0;
  const totalBonusReceivedFromUs = totalSumData?.bonus || 0;

  const currentMonthBalance = useMemo(
    () =>
      Math.ceil(
        (totalMtdPoints / totalMtdPointsAll) * parseFloat(currentMonthBudget)
      ).toFixed(2),
    [totalMtdPoints, totalMtdPointsAll, currentMonthBudget]
  );

  return (
    <div className="flex flex-col w-full">
      {companyName && (
        <div className="mb-6">
          <Typography variant="h4" fontWeight={500}>
            {t('label.welcome')} {companyName}
          </Typography>
        </div>
      )}

      <div className="grid grid-cols-4 grid-rows-2 gap-4">
        <div className="basis-[calc(25%-1rem)]">
          <CardStats
            classNames="bg-primary-50"
            boxShadowColor="#f3ced7"
            currentLabel={
              <Trans i18nKey="partner.total_current_month_budget" />
            }
            currentValue={
              <div>
                <span className="text-3xl mr-1">
                  {parseFloat(currentMonthBudget).toFixed(2)}
                </span>
                <Trans i18nKey="common.label.pln" />
              </div>
            }
            previousLabel={
              <Trans i18nKey="partner.total_previous_month_budget" />
            }
            previousValue={
              <div>
                <span className="text-sm mr-1 font-bold">
                  {parseFloat(previousMonthBudget).toFixed(2)}
                </span>
                <span className="text-xs mr-1 font-bold">
                  <Trans i18nKey="common.label.pln" />
                </span>
              </div>
            }
            icon={
              <div className="bg-primary-500 flex flex-row items-center h-12 w-12 justify-center rounded-full">
                <WalletIcon
                  style={{
                    fontSize: 25,
                    color: '#ffffff',
                  }}
                />
              </div>
            }
          />
        </div>
        <div className="basis-[calc(25%-1rem)]">
          <CardStats
            currentLabel={<Trans i18nKey="partner.current_month_balance" />}
            currentValue={
              <div>
                <span className="text-3xl mr-1">{currentMonthBalance}</span>
                <Trans i18nKey="common.label.pln" />
              </div>
            }
            previousLabel={<Trans i18nKey="partner.previous_month_balance" />}
            previousValue={`${currentMonthBalance}`}
            icon={
              <div className="bg-gray-50 flex flex-row items-center h-12 w-12 justify-center rounded-full">
                <WalletIcon
                  style={{
                    fontSize: 25,
                    color: '#fc0841',
                  }}
                />
              </div>
            }
          />
        </div>
        <div className="basis-[calc(25%-1rem)]">
          <CardStats
            currentLabel={<Trans i18nKey="label.total_views" />}
            currentValue={
              <span className="text-4xl mr-1">{totalMtdPassengers}</span>
            }
            icon={
              <div className="bg-gray-50 flex flex-row items-center h-12 w-12 justify-center rounded-full">
                <GroupsIcon
                  style={{
                    fontSize: 25,
                    color: '#fc0841',
                  }}
                />
              </div>
            }
          />
        </div>
        <div className="basis-[calc(25%-1rem)]">
          <CardStats
            currentLabel={<Trans i18nKey="label.total_screens_online" />}
            currentValue={
              <span className="text-4xl mr-1">{totalScreensOnline}</span>
            }
            icon={
              <div className="bg-gray-50 flex flex-row items-center h-12 w-12 justify-center rounded-full">
                <CastIcon style={{ fontSize: 25, color: '#fc0841' }} />
              </div>
            }
          />
        </div>
        <div className="basis-[calc(25%-1rem)]">
          <CardStats
            currentLabel={<Trans i18nKey="label.total_points_collected" />}
            currentValue={
              <span className="text-4xl mr-1">{totalMtdPoints}</span>
            }
            icon={
              <div className="bg-gray-50 flex flex-row items-center h-12 w-12 justify-center rounded-full">
                <DataUsageIcon style={{ fontSize: 25, color: '#fc0841' }} />
              </div>
            }
            classNames="hover:cursor-pointer hover:bg-slate-50"
          />
        </div>
        <div className="col-start-4 row-start-1 row-span-2">
          <Card
            className="bg-primary-600 text-white col-start-1"
            boxShadowColor="#dc345e"
          >
            <div className="w-full h-full flex flex-col items-center justify-center relative gap-6">
              <div className="flex flex-col items-center">
                <span className="text-white text-xl">
                  <Trans i18nKey="partner.total_you_made" />
                </span>
                <div>
                  <span className="text-6xl mr-1">
                    {(totalEarnedWithUs + totalBonusReceivedFromUs).toFixed(2)}
                  </span>
                  <span className="text-4xl mr-1">
                    <Trans i18nKey="common.label.pln" />
                  </span>
                </div>
              </div>
              <div className="flex flex-row items-center justify-center gap-4 ">
                <div className="text-primary-100 flex flex-col items-center">
                  <span className="text-xs">
                    <Trans i18nKey="partner.total_earned" />
                  </span>
                  <div className="flex flex-row items-end justify-center">
                    <span className="text-3xl mr-1">
                      {totalEarnedWithUs.toFixed(2)}
                    </span>
                    <span className="text-xl mr-1">
                      <Trans i18nKey="common.label.pln" />
                    </span>
                  </div>
                </div>
                <span className="text-2xl">+</span>
                <div className="text-primary-100">
                  <span className="text-xs">
                    <Trans i18nKey="partner.total_bonus_received" />
                  </span>
                  <div className="flex flex-row items-end justify-center">
                    <span className="text-3xl mr-1">
                      {totalBonusReceivedFromUs.toFixed(2)}
                    </span>
                    <span className="text-xl mr-1">
                      <Trans i18nKey="common.label.pln" />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/* 
      <div className="w-full py-8">
        {totalMtdPoints} / {totalMtdPointsAll}
      </div> */}

      <div className=" mt-7 mb-5">
        <TaxiTable />
      </div>
    </div>
  );
};
